import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import {
  fetchPurchasableCartCount,
  resolveBadgeCountFromEvent,
} from '@theme/stryxx-cart-count';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    let badgeCount = resolveBadgeCountFromEvent(event);

    if (badgeCount === null) {
      if (event.detail?.data?.source === 'stryxx-free-gift') return;
      try {
        badgeCount = await fetchPurchasableCartCount();
      } catch {
        return;
      }
    }

    if (badgeCount === this.currentCartCount) return;

    await this.renderCartBubble(badgeCount);
  };

  /**
   * Renders the cart bubble from the authoritative purchasable-item count.
   * @param {number} badgeCount - Purchasable items in cart (excludes free gifts).
   * @param {boolean} [animate=true] - Whether to play the add animation.
   */
  renderCartBubble = async (badgeCount, animate = true) => {
    const previousCount = this.currentCartCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', badgeCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', badgeCount === 0);

    this.currentCartCount = badgeCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', badgeCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(badgeCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || badgeCount === 0 || badgeCount === previousCount) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Reconciles the badge with the server when restored from bfcache or stale session data.
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;

    try {
      const badgeCount = await fetchPurchasableCartCount();
      if (badgeCount !== this.currentCartCount) {
        await this.renderCartBubble(badgeCount, false);
      }
    } catch {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
